import { useEffect, useRef, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { MAIN_API } from "../../../api/apilist";
import { getEventById } from "../../../api/api";
import {
  getResolvedMetaPixelId,
  buildMetaPurchasePayload,
  loadMetaPixel,
  trackMetaPixel,
} from "../../../helpers/metaPixel";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
  LoadingOutlined,
  ArrowLeftOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import "./Success.css"; // keep existing styles for non-success states
import "../../success/success-minimal.css";

export default function SuccessCloverPage() {
  const [status, setStatus] = useState("checking"); // checking | paid | failed | unknown
  const [isLoading, setIsLoading] = useState(false);
  const sessionId = typeof window !== "undefined" ? localStorage.getItem("checkoutSessionId") : null;
  const token = useSelector((s) => s.auth?.accessToken);
  const user = useSelector((s) => s.auth?.user);
  const navigate = useNavigate();
  const location = useLocation();
  const purchaseTrackedRef = useRef(false);

  // Detect reservation redirect and forward to reservation success page
  const reservationReturn = useMemo(() => {
    try {
      const sp = new URLSearchParams(location.search || "");
      const orderId = sp.get("orderId") || sp.get("orderID") || sp.get("order") || sp.get("oid") || sp.get("reservationCode") || "";
      const raw = sessionStorage.getItem("reservation:last");
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed?.flow === "reservas" && parsed?.reservationCode) {
        if (!orderId || String(parsed.reservationCode) === String(orderId)) {
          return parsed;
        }
      }
      if (sp.get("reservationCode") && sp.get("barId")) {
        return {
          flow: "reservas",
          reservationCode: sp.get("reservationCode"),
          barId: sp.get("barId"),
        };
      }
      return null;
    } catch (_) {
      return null;
    }
  }, [location.search]);

  useEffect(() => {
    if (reservationReturn) {
      navigate(`/reservas/success?code=${encodeURIComponent(String(reservationReturn.reservationCode))}`, { replace: true });
    }
  }, [reservationReturn, navigate]);

  /**
   * This page resolves the order from the Clover `checkoutSessionId` kept in
   * localStorage. A Fiserv return instead carries `orderId` in the query string
   * and never writes that key, so landing here — which happens when
   * CHECKOUT_SUCCESS_URL points at /payment/success — would show "Orden no
   * encontrada" to a buyer who already paid, and would skip the Purchase pixel.
   * Forward those returns to the page that knows how to read `orderId`.
   */
  const fiservReturnSearch = useMemo(() => {
    if (reservationReturn || sessionId) return null;
    const sp = new URLSearchParams(location.search || "");
    const orderId = sp.get("orderId") || sp.get("orderID") || sp.get("order") || sp.get("oid");
    return orderId ? location.search || `?orderId=${encodeURIComponent(orderId)}` : null;
  }, [reservationReturn, sessionId, location.search]);

  useEffect(() => {
    if (fiservReturnSearch) {
      navigate(`/payment/fiserv/success${fiservReturnSearch}`, { replace: true });
    }
  }, [fiservReturnSearch, navigate]);

  const trackPurchaseIfReady = async (orderInfo) => {
    if (purchaseTrackedRef.current || !orderInfo?.isReady || !orderInfo?.eventId) return;
    const storageKey = `zoco-meta-purchase:${orderInfo.orderId || orderInfo.orderNumber || sessionId || ""}`;
    try {
      if (storageKey && localStorage.getItem(storageKey)) {
        purchaseTrackedRef.current = true;
        return;
      }
    } catch (_) {}

    try {
      const eventRes = await getEventById(orderInfo.eventId);
      const eventInfo = eventRes?.data?.info?.event || {};
      const pixelId = getResolvedMetaPixelId(eventInfo);
      if (!loadMetaPixel(pixelId)) return;

      const { params, options } = buildMetaPurchasePayload(orderInfo);
      trackMetaPixel("Purchase", params, options);
      purchaseTrackedRef.current = true;
      try {
        if (storageKey) localStorage.setItem(storageKey, "1");
      } catch (_) {}
    } catch (_) {}
  };

  useEffect(() => {
    let timer;
    async function poll() {
      // Avoid flashing "Orden no encontrada" while the Fiserv redirect above runs.
      if (fiservReturnSearch) return;
      if (!sessionId) { setStatus("unknown"); return; }
      try {
        setIsLoading(true);
        const res = await fetch(`${MAIN_API}/api/app/order/successpayment`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const json = await res.json();
        if (json?.isSuccess) {
          await trackPurchaseIfReady(json?.info || {});
          setStatus("paid");
        } else {
          timer = setTimeout(poll, 2000);
        }
      } catch {
        timer = setTimeout(poll, 3000);
      } finally {
        setIsLoading(false);
      }
    }
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, token, fiservReturnSearch]);

  useEffect(() => {
    if (status === "paid") {
      const t = setTimeout(() => navigate("/myevents", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [status, navigate]);

  const firstName = (user?.name || "").split(" ")[0] || "";

  if (reservationReturn) {
    return null;
  }

  if (status === "paid") {
    return (
      <div className="success-minimal">
        <div className="success-minimal-card">
          <svg
            className="success-minimal-icon"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 10h6l6 30h28l6-18H20"
              stroke="#BAC819"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="26" cy="52" r="3" stroke="#BAC819" strokeWidth="3" />
            <circle cx="46" cy="52" r="3" stroke="#BAC819" strokeWidth="3" />
          </svg>
          <h2 className="success-minimal-title">
            ¡Listo{firstName ? `, ${firstName}` : ""}! Tu compra fue exitosa
          </h2>
          <p className="success-minimal-subtext">
            En unos segundos te vamos a redirigir a <strong>Mis eventos</strong> donde vas a poder ver tus tickets.
          </p>
          <button className="success-minimal-btn" onClick={() => navigate("/myevents")}>
            Ir ahora
          </button>
          <div className="success-minimal-note">
            <span>
              <i>i</i> Recordá que tu QR es dinámico y se actualizará automáticamente. Podrás transferir tickets 12hs antes del evento.
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Keep existing visuals for non-success states
  if (status === "checking" || isLoading) {
    return (
      <div className="payment-success-container checking-bg">
        <div className="payment-card fade-in-animation">
          <div className="payment-icon-container checking-icon">
            <LoadingOutlined className="payment-spinner" />
          </div>
          <h2 className="payment-title">Procesando tu pago</h2>
          <p className="payment-description">Estamos verificando tu transacción. Esto puede tomar unos momentos.</p>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="success-minimal">
        <div className="success-minimal-card">
          <svg
            className="success-minimal-icon"
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 10h6l6 30h28l6-18H20"
              stroke="#E53935"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line x1="22" y1="18" x2="30" y2="26" stroke="#E53935" strokeWidth="3" />
            <line x1="30" y1="18" x2="22" y2="26" stroke="#E53935" strokeWidth="3" />
            <circle cx="26" cy="52" r="3" stroke="#E53935" strokeWidth="3" />
            <circle cx="46" cy="52" r="3" stroke="#E53935" strokeWidth="3" />
          </svg>
          <h2 className="error-minimal-title">¡Ups! Algo salió mal</h2>
          <p className="error-minimal-subtext">No pudimos procesar el pago, intentá de nuevo en unos minutos.</p>
          <button className="success-minimal-btn" onClick={() => navigate("/cart")}>
            Reintentar pago
          </button>
          <div className="success-minimal-note">
            <span>
              <i>i</i> Si el problema persiste, contactá a <a href="/contact-us"><strong>nuestro soporte</strong></a>.
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (status === "unknown") {
    return (
      <div className="payment-success-container unknown-bg">
        <div className="payment-card">
          <div className="payment-icon-container unknown-icon">
            <QuestionCircleOutlined className="payment-icon" />
          </div>
          <h2 className="payment-title">Orden no encontrada</h2>
          <p className="payment-description">No pudimos localizar información sobre tu orden.</p>
          <button className="payment-button secondary-button" onClick={() => navigate("/")}>
            <ArrowLeftOutlined className="payment-button-icon" /> Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-success-container checking-bg">
      <div className="payment-card">
        <h2 className="payment-title">Procesando tu pago…</h2>
      </div>
    </div>
  );
}
